// Google Maps Service - Centralized Google Maps API management
class GoogleMapsService {
  constructor() {
    this.isLoaded = false;
    this.isLoading = false;
    this.loadPromise = null;
    this.listeners = new Set();
  }

  // Load Google Maps API
  async loadGoogleMaps() {
    if (this.isLoaded) {
      return Promise.resolve();
    }

    if (this.isLoading) {
      return this.loadPromise;
    }

    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      throw new Error('Google Maps API key not found. Please set VITE_GOOGLE_MAPS_API_KEY environment variable.');
    }

    this.isLoading = true;
    this.loadPromise = new Promise((resolve, reject) => {
      // Check if already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        this.isLoaded = true;
        this.isLoading = false;
        resolve();
        return;
      }

      // Remove any existing script to avoid conflicts
      const existingScript = document.getElementById('google-maps-script');
      if (existingScript) {
        existingScript.remove();
      }

      // Create and load the script
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.isLoaded = true;
        this.isLoading = false;
        this.notifyListeners();
        resolve();
      };

      script.onerror = () => {
        this.isLoading = false;
        this.loadPromise = null;
        reject(new Error('Failed to load Google Maps API'));
      };

      document.head.appendChild(script);
    });

    return this.loadPromise;
  }

  // Add listener for when maps are loaded
  addLoadListener(callback) {
    if (this.isLoaded) {
      callback();
    } else {
      this.listeners.add(callback);
    }
  }

  // Remove load listener
  removeLoadListener(callback) {
    this.listeners.delete(callback);
  }

  // Notify all listeners that maps are loaded
  notifyListeners() {
    this.listeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in Google Maps load listener:', error);
      }
    });
    this.listeners.clear();
  }

  // Check if Google Maps is loaded
  isGoogleMapsLoaded() {
    return this.isLoaded && window.google && window.google.maps && window.google.maps.places;
  }

  // Create autocomplete instance
  createAutocomplete(inputElement, options = {}) {
    if (!this.isGoogleMapsLoaded()) {
      throw new Error('Google Maps not loaded yet');
    }

    const defaultOptions = {
      types: ['address'],
      componentRestrictions: { country: 'us' }
    };

    return new window.google.maps.places.Autocomplete(
      inputElement, 
      { ...defaultOptions, ...options }
    );
  }
}

// Create singleton instance
const googleMapsService = new GoogleMapsService();

export default googleMapsService;
